import PropType from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useState } from 'react'
import { saveFifthStep } from '@store/reducers/sitterRequest'
import { postData } from '@services/requests'
import sitterRequestTest from '@assets/sitterRequestData'
import convertURLToFile from '@utils/convertUrlToFile'
import ErrorNoCard from '@common/error/ErrorNoCard'
import { useMutation } from '@tanstack/react-query'
import { useGetProfile } from '@services/user'

function FifthStep({ refetch }) {
  const dispatch = useDispatch()
  const { firstStep, secondStep, thirdStep, fourthStep, fifthStep } =
    useSelector((state) => state.sitterRequest)
  const { data: user } = useGetProfile()
  const [error, setError] = useState(null)

  const {
    isPending: postIsPending,
    isError: postIsError,
    error: postError,
    mutate: postSitterRequest,
  } = useMutation({
    mutationFn: async () => {
      const fileUrl = localStorage.getItem('certificateURL')
      const file = await convertURLToFile(fileUrl)
      const formData = new FormData()
      formData.append('file', file)
      formData.append('communes', JSON.stringify(firstStep.communes))
      formData.append('description', firstStep.description)
      formData.append('bankName', thirdStep.bank)
      formData.append('bankAccountType', thirdStep.type)
      formData.append('bankAccountNumber', thirdStep.accountNumber)
      const data = await postData(`sitter-request/${user.id}`, formData, {
        'Content-Type': 'multipart/form-data',
      })
      return data
    },
    onSuccess: () => {
      refetch()
      localStorage.removeItem('certificateURL')
    },
    onSettled: () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    },
  })

  const handleSitterRequest = () => {
    setError(null)
    if (firstStep.communes.length === 0 || firstStep.description === '') {
      setError('Debes completar el primer paso')
      return null
    }
    if (!secondStep.certificate) {
      setError('Debes completar el segundo paso')
      return null
    }
    if (
      thirdStep.bank === '' ||
      thirdStep.type === '' ||
      thirdStep.accountNumber === ''
    ) {
      setError('Debes completar el tercer paso')
      return null
    }
    if (
      !fourthStep.firstAnswer.correct ||
      !fourthStep.secondAnswer.correct ||
      !fourthStep.thirdAnswer.correct ||
      !fourthStep.fourthAnswer.correct ||
      !fourthStep.fifthAnswer.correct ||
      !fourthStep.sixthAnswer.correct
    ) {
      setError('Debes completar el cuarto paso')
      return null
    }
    if (!fifthStep.accepted) {
      setError('Debes aceptar las habilidades básicas')
      return null
    }
    postSitterRequest()
  }

  return (
    <div className="flex flex-col gap-12 p-4 text-center sm:w-[75%]">
      <div className="flex flex-col gap-6 text-left">
        {error && <ErrorNoCard message={ErrorNoCard} />}
        {postIsError && <ErrorNoCard message={postError.message} />}
        <h1 className="text-center font-tex-bold text-2xl text-grey-base lg:text-3xl xl:text-4xl">
          Código Pets-Okey
        </h1>
        <div className="space-y-4">
          <h2 className="mb-4 font-mont-semi text-base sm:text-xl">
            {sitterRequestTest.code.title}
          </h2>
          <div className="flex flex-col gap-4">
            {sitterRequestTest.code.options.map((option, index) => (
              <div key={index} className="flex flex-col gap-2">
                <h3 className="font-mont-semi">{option.title}</h3>
                <p>{option.subTitle}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex flex-row items-center gap-4">
        <input
          id="comments"
          aria-describedby="comments-description"
          name="comments"
          type="checkbox"
          className="h-6 w-6 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
          onChange={(e) => dispatch(saveFifthStep(e.target.checked))}
        />
        <p className="text-lg">
          {' '}
          Cuento con las habilidades básicas señaladas para ser sitter
        </p>
      </div>
      <button
        type="submit"
        disabled={postIsPending || !fifthStep.accepted}
        onClick={handleSitterRequest}
        className={`flex w-full items-center justify-center rounded-md bg-blue-500 px-3.5 py-2.5 font-mont-bold text-sm text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 ${
          postIsPending || !fifthStep.accepted
            ? 'cursor-not-allowed opacity-50'
            : 'hover:bg-blue-600'
        }`}
      >
        {postIsPending ? (
          <svg
            aria-hidden="true"
            className="h-6 w-6 animate-spin fill-blue-500 text-white"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
        ) : (
          <span>Postular como sitter</span>
        )}
      </button>
    </div>
  )
}

FifthStep.propTypes = {
  refetch: PropType.func,
}

export default FifthStep
